import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ChevronLeft, User, LayoutGrid, Table, Map } from "lucide-react";
import { IEstablishment } from "../../interfaces/establishment";
import { getEstablishments, getMultiSiteFounderNeighbors } from "../../interfaces/establishments";
import SignalCard from "../../components/signal/SignalCard";
import SignalTable from "../../components/signal/SignalTable";
import SignalMap from "../../components/signal/SignalMap";

export default function FounderSignals() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Support both ?firstName=X&lastName=Y (legacy) and ?founderName=X Y (new)
    const founderNameParam = searchParams.get("founderName") || "";
    const firstName = searchParams.get("firstName") || "";
    const lastName = searchParams.get("lastName") || "";
    const founderName = founderNameParam || `${firstName} ${lastName}`.trim();

    const [establishments, setEstablishments] = useState<IEstablishment[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'grid' | 'table' | 'map'>('table');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 12;
    const [neighbors, setNeighbors] = useState<{ prev: string | null; next: string | null; total: number; index: number } | null>(null);

    useEffect(() => {
        if (founderName) {
            fetchFounderEstablishments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [founderName, page]);

    useEffect(() => {
        if (founderName) {
            getMultiSiteFounderNeighbors(founderName).then(setNeighbors).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [founderName]);

    const fetchFounderEstablishments = async () => {
        try {
            setLoading(true);
            const result = await getEstablishments({
                founderName,
                page,
                limit,
                sortBy: "signalDate",
                order: "desc",
                is_active: "all",
                hasContactInfo: "false",
            });
            setEstablishments(result.establishments || []);
            setTotalPages(result.totalPages || 1);
        } catch (error) {
            console.error("Failed to fetch founder establishments:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveToggle = (id: string, saved: boolean) => {
        const userId = localStorage.getItem("userId") || "";
        setEstablishments(prev =>
            prev.map(est => {
                if (est._id !== id) return est;
                const savedBy: string[] = (est as any).savedBy || [];
                return {
                    ...est,
                    savedBy: saved ? [...savedBy, userId] : savedBy.filter((s: string) => s !== userId),
                };
            })
        );
    };

    if (!founderName) {
        return (
            <div className="min-h-screen bg-canvas font-sans">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
                    <div className="text-center">
                        <p className="text-ink-muted">Informations du fondateur manquantes</p>
                        <button
                            onClick={() => navigate("/signals")}
                            className="mt-4 inline-flex items-center justify-center rounded-lg bg-ink px-4 py-2 font-sans font-medium text-ink-inverse transition-colors duration-150 hover:bg-ink/90"
                        >
                            Retour aux signaux
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-2">
                {/* Header */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-ink-muted hover:text-ink mb-4 text-sm transition-colors duration-150"
                >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Retour</span>
                </button>

                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-ink flex items-center justify-center">
                            <User className="w-5 h-5 text-ink-inverse" />
                        </div>
                        <div className="flex-1">
                            <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">
                                Établissements de {founderName}
                            </h1>
                            <p className="text-sm text-ink-muted">
                                {establishments.length} établissement{establishments.length > 1 ? 's' : ''} trouvé{establishments.length > 1 ? 's' : ''}
                                {neighbors && neighbors.total > 0 && (
                                    <span className="ml-2 text-ink-faint">· {neighbors.index + 1} / {neighbors.total}</span>
                                )}
                            </p>
                        </div>
                        {neighbors && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate(`/signals/founder?founderName=${encodeURIComponent(neighbors.prev!)}`)}
                                    disabled={!neighbors.prev}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-lg text-sm text-ink-muted hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Précédent
                                </button>
                                <button
                                    onClick={() => navigate(`/signals/founder?founderName=${encodeURIComponent(neighbors.next!)}`)}
                                    disabled={!neighbors.next}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-lg text-sm text-ink-muted hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                >
                                    Suivant
                                    <ChevronLeft className="h-4 w-4 rotate-180" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* View Toggle */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2 bg-surface-sunken rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                viewMode === 'grid' ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'
                            }`}
                        >
                            <LayoutGrid className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                viewMode === 'table' ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'
                            }`}
                        >
                            <Table className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('map')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                viewMode === 'map' ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'
                            }`}
                        >
                            <Map className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-ink-muted text-sm">Chargement des établissements...</div>
                    </div>
                ) : establishments.length === 0 ? (
                    <div className="text-center py-12">
                        <User className="h-12 w-12 text-ink-faint mx-auto mb-4" />
                        <p className="text-ink-muted mb-2">Aucun établissement trouvé pour ce fondateur</p>
                        <p className="text-sm text-ink-muted">
                            Il semble que {founderName} n'ait pas d'établissements enregistrés.
                        </p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {establishments.map(est => (
                                    <SignalCard
                                        key={est._id}
                                        signal={est}
                                        onSaveToggle={handleSaveToggle}
                                        focusPersonName={founderName}
                                    />
                                ))}
                            </div>
                        ) : viewMode === 'map' ? (
                            <SignalMap signals={establishments} onSaveToggle={handleSaveToggle} />
                        ) : (
                            <SignalTable signals={establishments} onSaveToggle={handleSaveToggle} focusPersonName={founderName} />
                        )}

                        {totalPages > 1 && (
                            <div className="mt-6 flex items-center justify-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-3 py-1 border border-border rounded-lg text-sm text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-sunken transition-colors duration-150"
                                >
                                    Précédent
                                </button>
                                <span className="text-sm text-ink-muted">Page {page} sur {totalPages}</span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="px-3 py-1 border border-border rounded-lg text-sm text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-sunken transition-colors duration-150"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
